export interface SectorPathData {
  name: string;
  path: string;
  type: 'path' | 'rect';
  rectData?: {
    x: number;
    y: number;
    width: number;
    height: number;
    rx?: number;
    ry?: number;
  };
}

export interface SectorLabelData {
  name: string;
  x: number;
  y: number;
  fontSize?: number;
  text: string;
}

export function extractSectorPaths(svgText: string): SectorPathData[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, 'image/svg+xml');
  const result: SectorPathData[] = [];

  const pathElements = doc.querySelectorAll('path[data-name^="SectorPreview."]');
  pathElements.forEach(path => {
    const name = path.getAttribute('data-name')?.replace('SectorPreview.', '') || 'Unknown';
    const pathData = path.getAttribute('d');
    if (pathData) {
      result.push({ name, path: pathData, type: 'path' });
    }
  });

  const rectElements = doc.querySelectorAll('rect[data-name^="SectorPreview."]');
  rectElements.forEach(rect => {
    const name = rect.getAttribute('data-name')?.replace('SectorPreview.', '') || 'Unknown';
    processRectElement(rect, name, result);
  });

  const groupElements = doc.querySelectorAll('g[data-name^="SectorPreview."]');
  groupElements.forEach(group => {
    const name = group.getAttribute('data-name')?.replace('SectorPreview.', '') || 'Unknown';
    const rect = group.querySelector('rect');
    const path = group.querySelector('path');

    if (rect) {
      processRectElement(rect, name, result);
    } else if (path) {
      const pathData = path.getAttribute('d');
      if (pathData) {
        result.push({ name, path: pathData, type: 'path' });
      }
    }
  });

  return result;
}

export function extractLodgeLabels(svgText: string): SectorLabelData[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, 'image/svg+xml');
  const labels: SectorLabelData[] = [];

  const textNodes = doc.querySelectorAll('text');
  textNodes.forEach(textEl => {
    const content = (textEl.textContent || '').replace(/\s+/g, ' ').trim();
    const loweredContent = content.toLowerCase();
    const isLodgeLike = loweredContent.includes('ложа');
    const isRoyalVipLike = loweredContent.includes('royal vip');
    if (!isLodgeLike && !isRoyalVipLike) return;

    const position = extractTextPosition(textEl);
    if (!position) return;
    const { x, y } = position;

    let fontSize: number | undefined;
    const attrSize = textEl.getAttribute('font-size');
    if (attrSize) {
      const parsed = parseFloat(attrSize);
      if (!Number.isNaN(parsed)) fontSize = parsed;
    }
    const styleAttr = textEl.getAttribute('style');
    if (!fontSize && styleAttr) {
      const match = styleAttr.match(/font-size:\s*([\d.]+)px/i);
      if (match) {
        const parsed = parseFloat(match[1]);
        if (!Number.isNaN(parsed)) fontSize = parsed;
      }
    }
    if (!fontSize) {
      const tspan = textEl.querySelector('tspan');
      const tspanStyleAttr = tspan?.getAttribute('style');
      if (tspanStyleAttr) {
        const match = tspanStyleAttr.match(/font-size:\s*([\d.]+)px/i);
        if (match) {
          const parsed = parseFloat(match[1]);
          if (!Number.isNaN(parsed)) fontSize = parsed;
        }
      }
    }

    labels.push({ name: content, x, y, fontSize, text: content });
  });

  return labels;
}

function extractTextPosition(textEl: Element): { x: number; y: number } | null {
  const directX = parseFiniteNumber(textEl.getAttribute('x'));
  const directY = parseFiniteNumber(textEl.getAttribute('y'));
  if (directX !== null && directY !== null) return { x: directX, y: directY };

  const tspan = textEl.querySelector('tspan');
  const tspanX = parseFiniteNumber(tspan?.getAttribute('x'));
  const tspanY = parseFiniteNumber(tspan?.getAttribute('y'));
  if (tspanX !== null && tspanY !== null) return { x: tspanX, y: tspanY };

  const transform = textEl.getAttribute('transform') || '';
  const matrixMatch = transform.match(
    /matrix\(\s*[-+]?\d*\.?\d+(?:e[-+]?\d+)?[\s,]+[-+]?\d*\.?\d+(?:e[-+]?\d+)?[\s,]+[-+]?\d*\.?\d+(?:e[-+]?\d+)?[\s,]+[-+]?\d*\.?\d+(?:e[-+]?\d+)?[\s,]+([-+]?\d*\.?\d+(?:e[-+]?\d+)?)[\s,]+([-+]?\d*\.?\d+(?:e[-+]?\d+)?)\s*\)/i
  );
  if (matrixMatch) {
    const x = parseFiniteNumber(matrixMatch[1]);
    const y = parseFiniteNumber(matrixMatch[2]);
    if (x !== null && y !== null) return { x, y };
  }

  const translateMatch = transform.match(
    /translate\(\s*([-+]?\d*\.?\d+(?:e[-+]?\d+)?)(?:[\s,]+([-+]?\d*\.?\d+(?:e[-+]?\d+)?))?\s*\)/i
  );
  if (translateMatch) {
    const x = parseFiniteNumber(translateMatch[1]);
    const y = parseFiniteNumber(translateMatch[2] ?? '0');
    if (x !== null && y !== null) return { x, y };
  }

  return null;
}

function parseFiniteNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function processRectElement(rect: Element, name: string, result: SectorPathData[]) {
  const x = parseFloat(rect.getAttribute('x') || '0');
  const y = parseFloat(rect.getAttribute('y') || '0');
  const width = parseFloat(rect.getAttribute('width') || '0');
  const height = parseFloat(rect.getAttribute('height') || '0');
  const rx = rect.getAttribute('rx') ? parseFloat(rect.getAttribute('rx') || '0') : undefined;
  const ry = rect.getAttribute('ry') ? parseFloat(rect.getAttribute('ry') || '0') : undefined;

  const pathData =
    `M${x},${y + (ry || 0)} ` +
    (rx ? `a${rx},${ry || rx} 0 0 1 ${rx},-${ry || rx} ` : '') +
    `h${width - (rx ? rx * 2 : 0)}` +
    (rx ? `a${rx},${ry || rx} 0 0 1 ${rx},${ry || rx} ` : '') +
    `v${height - (ry ? ry * 2 : 0)}` +
    (rx ? `a${rx},${ry || rx} 0 0 1 -${rx},${ry || rx} ` : '') +
    `h-${width - (rx ? rx * 2 : 0)}` +
    (rx ? `a${rx},${ry || rx} 0 0 1 -${rx},-${ry || rx} ` : '') +
    'z';

  result.push({
    name,
    path: pathData,
    type: 'rect',
    rectData: { x, y, width, height, rx, ry },
  });
}
